import {
  Modal,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@ltvco/refresh-lib/theme';
import {
  useAccount,
  useSession,
  invalidateSession,
  closeSnackbar,
} from '@ltvco/refresh-lib/v1';
import { useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { LogoutButton } from 'components/DeepSearch/components/ProfileModal/LogoutButton';
import { NavigationSection } from 'components/DeepSearch/components/ProfileModal/NavigationSection';
import {
  ReportUsageSection,
  type ReportUsage,
} from 'components/DeepSearch/components/ProfileModal/ReportUsageSection';
import {
  getModalStyles,
  getContainerBoxStyles,
} from 'components/DeepSearch/components/ProfileModal/styles';
import { UpgradeSection } from 'components/DeepSearch/components/ProfileModal/UpgradeSection';
import { UserInfoSection } from 'components/DeepSearch/components/ProfileModal/UserInfoSection';
import { DEEPSEARCH_ROUTES } from 'components/DeepSearch/constants';
import { goToLogin } from 'utils/goToLogin';

type ProfileModalProps = {
  open: boolean;
  onClose: () => void;
  onAccountClick?: () => void;
  onUpgradePlanClick?: () => void;
  onHelpClick?: () => void;
  onFeedbackClick?: () => void;
  onLogout?: () => void;
  // When provided, the report usage meter renders inside the modal. Callers that
  // don't pass it (e.g. ChatBV) get the modal unchanged.
  reportUsage?: ReportUsage;
};

export const ProfileModal = ({
  open,
  onClose,
  onAccountClick,
  onUpgradePlanClick,
  onHelpClick,
  onFeedbackClick,
  onLogout,
  reportUsage,
}: ProfileModalProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account } = useAccount(isAuthenticated);
  const {
    links: { FaqLink },
  } = constants;

  const handleAccountClick = () => {
    onClose();
    if (onAccountClick) {
      onAccountClick();
    } else {
      navigate(DEEPSEARCH_ROUTES.account);
    }
  };

  const handleHelpClick = () => {
    onClose();
    onHelpClick?.();
  };

  const handleFeedbackClick = () => {
    onClose();
    onFeedbackClick?.();
  };

  const handleUpgradeClick = () => {
    onClose();
    onUpgradePlanClick?.();
  };

  const handleLearnMoreClick = () => {
    window.open(FaqLink, '_blank');
  };

  const handleLogoutClick = async () => {
    onClose();
    if (onLogout) {
      onLogout();
    } else {
      await invalidateSession();
      closeSnackbar();
      goToLogin('https://www.beenverified.com');
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={getModalStyles(isMobile)}>
      <Box sx={getContainerBoxStyles(theme)}>
        <UserInfoSection account={account} />

        {reportUsage && (
          <>
            <Divider sx={{ borderColor: theme.palette.divider }} />
            <ReportUsageSection usage={reportUsage} />
          </>
        )}

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <NavigationSection
          onAccountClick={handleAccountClick}
          onHelpClick={handleHelpClick}
          onFeedbackClick={handleFeedbackClick}
        />

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <UpgradeSection
          onUpgradeClick={handleUpgradeClick}
          onLearnMoreClick={handleLearnMoreClick}
        />

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <LogoutButton onLogout={handleLogoutClick} />
      </Box>
    </Modal>
  );
};
